import axios, { type AxiosResponse } from 'axios';

const path = 'admin/ai/';
const pluginName = 'aiplugin';
const aiMenuButton = 'aimenu';
const aiContextToolbar = 'aitoolbar';

interface Language {
    code: string;
    name: string;
}

interface TextStyle {
    code: string;
    name: string;
}

interface TextTone {
    code: string;
    name: string;
}

const AiPlugin = (tinymce: any, options: any = {}): any =>
{
    const aiStore = useAiSettingsStore();
    const isEnabled = typeof options.enabled === 'object' && options.enabled !== null ?
        options.enabled.value === true :
        options.enabled === true;

    tinymce.PluginManager.add(pluginName, (editor: any) =>
    {

        if (!isEnabled)
        {
            return;
        }

        try
        {
            const availableLanguages = ref<Language[]>([
                { code: 'en', name: $t('[[[Angielski]]]') },
                { code: 'de', name: $t('[[[Niemiecki]]]') },
                { code: 'fr', name: $t('[[[Francuski]]]') },
                { code: 'pl', name: $t('[[[Polski]]]') },
            ]);

            const textStyles = ref<TextStyle[]>([
                { code: 'prosty', name: $t('[[[Prosty]]]') },
                { code: 'rozbudowany', name: $t('[[[Rozbudowany]]]') },
                { code: 'zaawansowany', name: $t('[[[Zaawansowany]]]') }
            ]);

            const textTones = ref<TextTone[]>([
                { code: 'formalny', name: $t('[[[Formalny]]]') },
                { code: 'nieformalny', name: $t('[[[Nieformalny]]]') },
                { code: 'entuzjastyczny', name: $t('[[[Entuzjastyczny]]]') },
                { code: 'neutralny', name: $t('[[[Neutralny]]]') },
            ]);

            const callBackendApi = async (payload: TextEditorPayload): Promise<AxiosResponse<TextEditorResponse>> =>
            {
                const model = aiStore.getDefaultModelByType(AiModelTypes.TEXT);

                return await AiCoreTextEditorService.getTextEditorPromise(
                    {
                        provider: model.aiProviderName,
                        owner: model.owner,
                        name: model.name
                    }, payload);
            };

            const handleTextGeneration = () =>
            {
                const dialogApi = editor.windowManager.open({
                    title: $t('[[[Generowanie treści]]]'),
                    size: 'large',
                    body: {
                        type: 'panel',
                        items: [
                            {
                                type: 'htmlpanel',
                                html: `<div id="ai-generated-content" style="margin-top: 10px; max-height: 300px; overflow-y: auto; min-height: 200px;"><em style="color: #999;">${$t('[[[Tutaj pojawi się wygenerowana treść...]]]')}</em></div>`
                            },
                            {
                                type: 'htmlpanel',
                                html: '<div style="flex: 1; min-height: 20px;"></div>'
                            },
                            {
                                type: 'htmlpanel',
                                html: `
                                <div style="border-top: 1px solid #ccc; padding-top: 10px; margin-top: 10px; display: flex; align-items: center;">
                                    <div style="flex: 1; padding-right: 10px; position: relative;">
                                        <input id="prompt-input" style="width: 100%; padding: 8px 10px; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box;" placeholder="${$t('[[[Wpisz polecenie...]]]')}" />
                                    </div>
                                    <button id="send-btn" type="button" class="tox-button tox-button--icon" style="margin-left: 5px;">
                                        <span class="tox-icon tox-button-icon__wrap">
                                            <svg width="24" height="24" focusable="false">
                                                <path d="M2 12h19M13 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none"></path>
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                                `
                            }
                        ]
                    },
                    buttons: [
                        {
                            type: 'cancel',
                            text: $t('[[[Anuluj]]]')
                        },
                        {
                            type: 'submit',
                            text: $t('[[[Wstaw]]]'),
                            primary: true
                        }
                    ],
                    onSubmit: (api: any) =>
                    {
                        if (generatedContent)
                        {
                            editor.undoManager.transact(() =>
                            {
                                editor.insertContent(generatedContent);
                            });
                        }

                        api.close();
                    }
                });

                let generatedContent = '';
                let isInitialGeneration = true;

                setTimeout(() =>
                {
                    const sendButton = document.getElementById('send-btn');
                    const promptInput = document.getElementById('prompt-input');

                    if (sendButton && promptInput)
                    {
                        sendButton.addEventListener('click', (event) =>
                        {
                            event.preventDefault();

                            const inputValue = (promptInput as HTMLInputElement).value;

                            if (!inputValue)
                            {
                                editor.windowManager.alert($t('[[[Wprowadź prompt]]]'));

                                return;
                            }

                            const contentElement = document.getElementById('ai-generated-content');

                            if (contentElement)
                            {
                                contentElement.innerHTML = `<em>${$t('[[[Generowanie treści...]]]')}</em>`;
                            }

                            const payload: TextEditorPayload = isInitialGeneration ?
                                {
                                    Tool: TextEditorTools.TextGeneration,
                                    TextGeneration: { Prompt: inputValue }
                                } :
                                {
                                    Tool: TextEditorTools.CustomPrompt,
                                    CustomPrompt: {
                                        Text: generatedContent,
                                        Prompt: inputValue
                                    }
                                };

                            editor.setProgressState(true);
                            callBackendApi(payload)
                                .then(apiResponse =>
                                {
                                    if (apiResponse && apiResponse.data)
                                    {
                                        generatedContent = apiResponse.data.message;

                                        if (contentElement)
                                        {
                                            contentElement.innerHTML = generatedContent;
                                        }

                                        if (isInitialGeneration)
                                        {
                                            isInitialGeneration = false;
                                            (promptInput as HTMLInputElement).value = '';
                                            (promptInput as HTMLInputElement).placeholder = $t('[[[Wpisz modyfikację np. "zrób krótsze", "dodaj więcej przykładów"...]]]');
                                        }
                                    }
                                })
                                .catch(error =>
                                {
                                    error.message
                                    editor.notificationManager.open({
                                        text: $t(error.message),
                                        type: 'error'
                                    });

                                    if (contentElement)
                                    {
                                        contentElement.innerHTML = `<em>${$t('[[[Wystąpił błąd]]]')}</em>`;
                                    }
                                })
                                .finally(() =>
                                {
                                    editor.setProgressState(false);
                                });
                        });

                        promptInput.addEventListener('keydown', (event) =>
                        {
                            if (event.key === 'Enter')
                            {
                                event.preventDefault();
                                sendButton.click();
                            }
                        });
                    }

                    const initialPromptField = dialogApi.getData().prompt;

                    if (initialPromptField && promptInput)
                    {
                        (promptInput as HTMLInputElement).value = initialPromptField;
                    }
                }, 100);
            };


            const handleAIAction = async (
                actionType: TextEditorTools,
                selectedText: string,
                targetLanguage: string | null = null,
                textStyle: string | null = null,
                textTone: string | null = null
            ) =>
            {
                if (!selectedText)
                {
                    editor.windowManager.alert($t('[[[Zaznacz tekst, aby wykonać operację AI.]]]'));

                    return;
                }

                let payload: TextEditorPayload;

                switch (actionType)
                {
                    case TextEditorTools.Translation:
                        payload = {
                            Tool: TextEditorTools.Translation,
                            Translation: {
                                Text: selectedText,
                                Language: targetLanguage
                            }
                        };
                        break;
                    case TextEditorTools.CustomPrompt:
                        payload = {
                            Tool: TextEditorTools.CustomPrompt,
                            CustomPrompt: {
                                Text: selectedText,
                                Prompt: ''
                            }
                        };
                        break;
                    case TextEditorTools.TextExpand:
                        payload = {
                            Tool: TextEditorTools.TextExpand,
                            TextExpand: {
                                Text: selectedText
                            }
                        };
                        break;
                    case TextEditorTools.TextChange:
                        payload = {
                            Tool: TextEditorTools.TextChange,
                            TextChange: {
                                Text: selectedText,
                                Style: textStyle
                            }
                        };
                        break;
                    case TextEditorTools.TextTone:
                        payload = {
                            Tool: TextEditorTools.TextTone,
                            TextTone: {
                                Text: selectedText,
                                Tone: textTone
                            }
                        };
                        break;
                    case TextEditorTools.TextCorrection:
                        payload = {
                            Tool: TextEditorTools.TextCorrection,
                            TextCorrection: {
                                Text: selectedText
                            }
                        };
                        break;
                    case TextEditorTools.Summarization:
                        payload = {
                            Tool: TextEditorTools.Summarization,
                            Summarization: {
                                Text: selectedText
                            }
                        };
                        break;
                    case TextEditorTools.GrammarCheck:
                        payload = {
                            Tool: TextEditorTools.GrammarCheck,
                            GrammarCheck: {
                                Text: selectedText
                            }
                        };
                        break;
                    default:
                        editor.windowManager.alert($t('[[[Akcja nie jest obsługiwana.]]]'));

                        return;
                }

                const dialogApi = editor.windowManager.open({
                    title: 'AI Assistant',
                    size: 'large',
                    body: {
                        type: 'panel',
                        items: [
                            {
                                type: 'htmlpanel',
                                html: `
                                <div style="position: relative; margin-bottom: 10px; max-height: 150px; overflow-y: auto;">
                                    <div style="position: sticky; top: 0; background-color: white; padding: 5px 0; border-bottom: 1px solid #eee; z-index: 1;">
                                        <strong>${$t('[[[Oryginalny tekst]]]')}:</strong>
                                    </div>
                                    <div style="padding-top: 5px;">
                                        ${selectedText}
                                    </div>
                                </div>
                            `
                            },
                            {
                                type: 'htmlpanel',
                                html: `
                                <div id="ai-response" style="position: relative; max-height: 250px; overflow-y: auto;">
                                    <div style="position: sticky; top: 0; background-color: white; padding: 5px 0; border-bottom: 1px solid #eee; z-index: 1;">
                                        <strong>${$t('[[[Generowana odpowiedź]]]')}:</strong>
                                    </div>
                                    <div style="padding-top: 5px;">
                                        <span style="color: #999;">${$t('[[[Trwa generowanie...]]]')}</span>
                                    </div>
                                </div>
                            `
                            },
                            {
                                type: 'htmlpanel',
                                html: `
                                <div style="border-top: 1px solid #ccc; padding-top: 10px; margin-top: 10px; display: flex; align-items: center;">
                                    <div style="flex: 1; padding-right: 10px; position: relative;">
                                        <input id="custom-prompt-input" style="width: 100%; padding: 8px 10px; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box;" placeholder="${$t('[[[Wpisz polecenie...]]]')}" />
                                    </div>
                                    <button id="send-btn" type="button" class="tox-button tox-button--icon" style="margin-left: 5px;">
                                        <span class="tox-icon tox-button-icon__wrap">
                                            <svg width="24" height="24" focusable="false">
                                                <path d="M2 12h19M13 5l8 7-8 7" stroke="currentColor" stroke-width="2" fill="none"></path>
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                                `
                            }
                        ]
                    },
                    buttons: [
                        {
                            type: 'cancel',
                            text: $t('[[[Anuluj]]]')
                        },
                        {
                            type: 'submit',
                            text: $t('[[[Wstaw]]]'),
                            primary: true
                        }
                    ],
                    onSubmit: async (api: any) =>
                    {
                        if (generatedText)
                        {
                            editor.undoManager.transact(() =>
                            {
                                editor.selection.setContent(generatedText);
                            });
                            api.close();
                        }
                    }
                });

                let generatedText = '';

                setTimeout(() =>
                {
                    const generateButton = document.getElementById('send-btn');
                    const customPromptInput = document.getElementById('custom-prompt-input');

                    if (generateButton && customPromptInput)
                    {
                        generateButton.addEventListener('click', (event) =>
                        {
                            event.preventDefault();

                            const customPrompt = (customPromptInput as HTMLInputElement).value;

                            if (!customPrompt)
                            {
                                editor.windowManager.alert($t('[[[Wprowadź prompt]]]'));

                                return;
                            }

                            const textToModify = generatedText || selectedText;

                            const customPayload: TextEditorPayload = {
                                Tool: TextEditorTools.CustomPrompt,
                                CustomPrompt: {
                                    Text: textToModify,
                                    Prompt: customPrompt
                                }
                            };

                            editor.setProgressState(true);
                            callBackendApi(customPayload)
                                .then(apiResponse =>
                                {
                                    if (apiResponse && apiResponse.data)
                                    {
                                        generatedText = apiResponse.data.message;

                                        const responseElement = document.getElementById('ai-response');

                                        if (responseElement)
                                        {
                                            responseElement.innerHTML = `<strong>${$t('[[[Wygenerowana odpowiedź]]]')}:</strong><br/>${generatedText}`;
                                        }

                                        (customPromptInput as HTMLInputElement).value = '';
                                    }
                                })
                                .catch(error =>
                                {
                                    editor.windowManager.alert($t('[[[Wystąpił błąd podczas modyfikowania treści]]]'));
                                })
                                .finally(() =>
                                {
                                    editor.setProgressState(false);
                                });
                        });

                        customPromptInput.addEventListener('keydown', (event) =>
                        {
                            if (event.key === 'Enter')
                            {
                                event.preventDefault();
                                generateButton.click();
                            }
                        });
                    }
                }, 100);

                if (actionType !== TextEditorTools.CustomPrompt)
                {

                    editor.setProgressState(true);

                    try
                    {
                        const apiResponse = await callBackendApi(payload);

                        if (apiResponse && apiResponse.data)
                        {
                            generatedText = apiResponse.data.message;

                            const responseElement = document.getElementById('ai-response');

                            if (responseElement)
                            {
                                const contentDiv = responseElement.querySelector('div[style*="padding-top"]') ||
                                responseElement.lastElementChild;

                                if (contentDiv)
                                {
                                    contentDiv.innerHTML = generatedText;
                                }
                                else
                                {
                                    responseElement.innerHTML = `
                                    <div style="position: sticky; top: 0; background-color: white; padding: 5px 0; border-bottom: 1px solid #eee; z-index: 1;">
                                        <strong>${$t('[[[Generowana odpowiedź]]]')}:</strong>
                                    </div>
                                    <div style="padding-top: 5px;">
                                        ${generatedText}
                                    </div>
                                `;
                                }
                            }
                        }
                        else
                        {
                            editor.windowManager.alert($t('[[[Nie udało się uzyskać odpowiedzi z API.]]]'));
                            dialogApi.close();
                        }
                    }
                    catch (error: any)
                    {
                        editor.windowManager.alert($t(error.message));
                        dialogApi.close();
                    }
                    finally
                    {
                        editor.setProgressState(false);
                    }
                }
                else
                {
                    const responseElement = document.getElementById('ai-response');

                    if (responseElement)
                    {
                        const contentDiv = responseElement.querySelector('div[style*="padding-top"]') ||
                            responseElement.lastElementChild;

                        if (contentDiv)
                        {
                            contentDiv.innerHTML = `<span style="color: #999;">${$t('[[[Wpisz prompt i kliknij przycisk wyślij aby wygenerować odpowiedź...]]]')}</span>`;
                        }
                    }
                }
            };

            editor.ui.registry.addMenuButton(aiMenuButton, {
                text: 'AI Tools',
                icon: 'ai-prompt',
                fetch: (callback: any) =>
                {
                    const items = [
                        {
                            type: 'menuitem',
                            text: $t('[[[Generuj treść]]]'),
                            icon: 'ai',
                            onAction: () => handleTextGeneration()
                        },
                        {
                            type: 'menuitem',
                            text: $t('[[[Własne polecenie]]]'),
                            icon: 'comment',
                            onAction: () =>
                            {
                                handleAIAction(TextEditorTools.CustomPrompt, editor.selection.getContent({ format: 'text' }));
                            }
                        },
                        {
                            type: 'nestedmenuitem',
                            text: $t('[[[Edycja tekstu]]]'),
                            icon: 'edit-block',
                            getSubmenuItems: () => [
                                {
                                    type: 'menuitem',
                                    text: $t('[[[Rozbuduj tekst]]]'),
                                    onAction: () =>
                                        handleAIAction(TextEditorTools.TextExpand, editor.selection.getContent({ format: 'text' }))
                                },
                                {
                                    type: 'menuitem',
                                    text: $t('[[[Popraw merytorycznie]]]'),
                                    onAction: () =>
                                        handleAIAction(TextEditorTools.TextCorrection, editor.selection.getContent({ format: 'text' }))
                                },
                                {
                                    type: 'menuitem',
                                    text: $t('[[[Sprawdź gramatykę]]]'),
                                    onAction: () =>
                                        handleAIAction(TextEditorTools.GrammarCheck, editor.selection.getContent({ format: 'text' }))
                                },
                            ]
                        },
                        {
                            type: 'nestedmenuitem',
                            text: $t('[[[Tłumaczenie]]]'),
                            icon: 'translate',
                            getSubmenuItems: () =>
                                availableLanguages.value.map(lang => ({
                                    type: 'menuitem',
                                    text: lang.name,
                                    onAction: () =>
                                        handleAIAction(TextEditorTools.Translation, editor.selection.getContent({ format: 'text' }), lang.code)
                                }))
                        },
                        {
                            type: 'nestedmenuitem',
                            text: $t('[[[Styl Tekstu]]]'),
                            icon: 'template',
                            getSubmenuItems: () =>
                                textStyles.value.map(style => ({
                                    type: 'menuitem',
                                    text: style.name,
                                    onAction: () =>
                                        handleAIAction(TextEditorTools.TextChange, editor.selection.getContent({ format: 'text' }), null, style.code)
                                }))
                        },
                        {
                            type: 'nestedmenuitem',
                            text: $t('[[[Tonalność Tekstu]]]'),
                            icon: 'comment',
                            getSubmenuItems: () =>
                                textTones.value.map(tone => ({
                                    type: 'menuitem',
                                    text: tone.name,
                                    onAction: () =>
                                        handleAIAction(TextEditorTools.TextTone, editor.selection.getContent({ format: 'text' }), null, null, tone.code)
                                }))
                        },
                        {
                            type: 'menuitem',
                            text: $t('[[[Streszczenie]]]'),
                            icon: 'vertical-align',
                            onAction: () =>
                                handleAIAction(TextEditorTools.Summarization, editor.selection.getContent({ format: 'text' }))
                        }
                    ];

                    callback(items);
                },
            });

            editor.ui.registry.addContextToolbar(aiContextToolbar, {
                predicate: (node: any) => !editor.selection.isCollapsed(),
                items: aiMenuButton,
                position: 'selection',
                scope: 'node'
            });
        }
        catch (error)
        {
            editor.notificationManager.open({
                text: $t('[[[Wystąpił błąd podczas inicjalizacji pluginu AI.]]]'),
                type: 'error'
            });
        }
    });
};

export { AiPlugin, pluginName, aiMenuButton, aiContextToolbar };
